import { Body, Plane, Vec3, World } from 'cannon-es';
import { MathUtils, Vector3 } from 'three';
import { CHARACTER_TYPES, type CharacterKind } from '../config/characterConfig';
import { GAME_CONFIG } from '../config/gameConfig';
import { getLevel, LEVELS, type LevelDefinition } from '../config/levelConfig';
import { TOOL_CONFIG, TOOL_ORDER, type ToolId } from '../config/toolConfig';
import { Character } from '../characters/Character';
import { CharacterFactory } from '../characters/CharacterFactory';
import { RiverScene } from '../scene/RiverScene';
import { AudioSystem } from '../systems/AudioSystem';
import { evaluateRescue, type ProbabilityResult } from '../systems/ProbabilitySystem';
import { calculateScore } from '../systems/ScoreSystem';
import { StatisticsSystem } from '../systems/StatisticsSystem';
import { AutoSalvageFleet } from '../tools/AutoSalvageFleet';
import { InflatableBoat } from '../tools/InflatableBoat';
import { RescuePole } from '../tools/RescuePole';
import { RescueRope } from '../tools/RescueRope';
import type { RescueTool } from '../tools/RescueTool';
import { createRescueTool } from '../tools/ToolFactory';
import { UIController, type CustomSettings } from '../ui/UIController';
import { CharacterState, GameState, ToolState } from './GameState';
import { RandomManager } from './RandomManager';

const THROW_ORIGIN = new Vector3(13, 4.6, 15.2);
const ACCIDENTS = [
  '一块香蕉皮完成了它的职业使命。',
  '一只鸭子突然从镜头前飞过。',
  '拍照后退时，桥比想象中短。',
  '鱼很大，钓鱼者的准备很小。',
  '帽子飞走了，主人决定一起走。',
  '栏杆松了一下，场面立刻不松弛了。',
  '脚下那块地临时决定下班。',
  '蝴蝶毫发无损，追它的人掉下去了。',
];

type InteractionMode =
  | 'none'
  | 'ringPull'
  | 'ropeTighten'
  | 'ropePull'
  | 'ropeWrong'
  | 'vestSequence'
  | 'pole'
  | 'boatInflate'
  | 'boatSteer'
  | 'autoSalvage'
  | 'observe';

interface SessionFlags {
  accuracy: number;
  timing: number;
  injury: boolean;
  dazed: boolean;
  ropeError: boolean;
  vestError: boolean;
  severe: boolean;
}

const freshFlags = (): SessionFlags => ({
  accuracy: 0,
  timing: 0,
  injury: false,
  dazed: false,
  ropeError: false,
  vestError: false,
  severe: false,
});

declare global {
  interface Window {
    __RESCUE_GAME__?: {
      getState: () => string;
      startLevel: (id: string) => void;
      skipIntro: () => void;
      chooseTool: (id: ToolId) => void;
      setAim: (value: number) => void;
      setPower: (value: number) => void;
      simulateLanding: (id: ToolId, accuracy: number, hazard?: boolean) => void;
      setTimeLeft: (value: number) => void;
      forceOutcome: (success: boolean) => void;
      getSnapshot: () => Record<string, unknown>;
    };
  }
}

export class Game {
  readonly ui: UIController;
  readonly statistics = new StatisticsSystem();
  readonly audio = new AudioSystem();
  readonly random: RandomManager;
  readonly riverScene: RiverScene;
  readonly physics = new World({ gravity: new Vec3(0, -9.82, 0) });

  state = GameState.MENU;
  private previousState = GameState.MENU;
  private stateElapsed = 0;
  private totalTime = 0;
  private lastFrame = performance.now();
  private animationFrame = 0;
  private currentLevel: LevelDefinition = LEVELS[0];
  private customSettings: CustomSettings | null = null;
  private customCharacter: CharacterKind | 'random' = 'random';
  private character: Character | null = null;
  private activeTool: RescueTool | null = null;
  private selectedMethod: ToolId | 'observe' = 'lifeRing';
  private availableTools: ToolId[] = [...TOOL_ORDER];
  private usedTools: ToolId[] = [];
  private timeLeft = 15;
  private elapsedRescue = 0;
  private attempt = 1;
  private multiIndex = 1;
  private aimOffset = 0;
  private power = 0;
  private charging = false;
  private chargeDirection = 1;
  private interactionMode: InteractionMode = 'none';
  private interactionCount = 0;
  private interactionValue = 0;
  private ropeSafe = false;
  private vestStep = 0;
  private boatDirection = 0;
  private observationTimer = 0;
  private autoSalvageStart = new Vector3();
  private autoSalvageSoundPhase = '';
  private outcomePending: { success: boolean; probability: ProbabilityResult | null } | null = null;
  private outcomeRecorded = false;
  private flags = freshFlags();
  private audioEnabled = true;
  private highQuality = true;
  private showTrajectory = true;
  private allowSwitching = true;
  private debug = new URLSearchParams(location.search).has('debug');
  private testMode = new URLSearchParams(location.search).has('test');

  constructor(private readonly app: HTMLElement) {
    const seedParam = Number(new URLSearchParams(location.search).get('seed'));
    this.random = new RandomManager(Number.isFinite(seedParam) && seedParam > 0 ? seedParam : undefined);
    this.ui = new UIController(app);
    this.ui.bind(this.handleAction);
    this.ui.bindRangeOutputs();
    this.riverScene = new RiverScene(app, this.random, 'high');
    this.riverScene.cameraController.setMode('overview', true);
    this.physics.broadphase.useBoundingBoxes = true;
    const waterPlane = new Body({ mass: 0, shape: new Plane() });
    waterPlane.position.set(0, 0.2, 0);
    waterPlane.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
    this.physics.addBody(waterPlane);
    this.bindInputs();
    if (this.debug || this.testMode) this.installDebugApi();
    this.animationFrame = requestAnimationFrame(this.loop);
  }

  private installDebugApi(): void {
    window.__RESCUE_GAME__ = {
      getState: () => this.state,
      startLevel: (id) => this.startLevel(id),
      skipIntro: () => {
        if (!this.character) return;
        this.ui.hideIntro();
        this.character.placeInWater(this.currentLevel.distance);
        this.enterWater();
      },
      chooseTool: (id) => this.selectTool(id),
      setAim: (value) => {
        this.aimOffset = MathUtils.clamp(value, -1, 1);
      },
      setPower: (value) => {
        this.power = MathUtils.clamp(value, 0, 1);
        this.ui.setPower(this.power);
      },
      simulateLanding: (id, accuracy, hazard = false) => {
        if (!this.character) return;
        this.selectTool(id);
        this.ui.hideAim();
        this.riverScene.hideTrajectory();
        this.flags.accuracy = MathUtils.clamp(accuracy, 0, 1);
        if (id === 'lifeRing') {
          if (this.activeTool) this.activeTool.impactSpeed = hazard ? 18 : 4;
          this.landLifeRing(hazard ? 0.45 : accuracy > 0.4 ? 2 : 10);
        } else if (id === 'rope') {
          this.landRope();
        } else if (id === 'lifeVest') {
          this.landVest();
        }
      },
      setTimeLeft: (value) => {
        this.timeLeft = Math.max(0, value);
      },
      forceOutcome: (success) => this.finishRescue(success, null),
      getSnapshot: () => this.snapshot(),
    };
  }

  private bindInputs(): void {
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    const holdButton = this.ui.root.querySelector('#throw-button');
    holdButton?.addEventListener('pointerdown', this.onChargeStart);
    holdButton?.addEventListener('pointerup', this.onChargeEnd);
    holdButton?.addEventListener('pointercancel', this.onChargeEnd);
    holdButton?.addEventListener('contextmenu', (event) => event.preventDefault());
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private handleAction = (action: string, value?: string): void => {
    this.audio.initialize().catch(() => undefined);
    this.audio.click();
    switch (action) {
      case 'start-level':
        this.startLevel(value ?? 'calm');
        break;
      case 'start-custom':
        this.startCustom(this.ui.getCustomSettings());
        break;
      case 'open-panel':
        this.ui.openPanel(value as 'levels' | 'custom' | 'stats' | 'help', this.statistics.data);
        break;
      case 'close-panel':
        this.ui.closePanel();
        break;
      case 'home':
        this.goHome();
        break;
      case 'select-tool':
        this.selectTool(value as ToolId);
        break;
      case 'cancel-tool':
        this.cancelTool();
        break;
      case 'observe':
        this.observe();
        break;
      case 'pause':
        this.pause();
        break;
      case 'resume':
        this.resume();
        break;
      case 'replay':
        this.startLevel(this.currentLevel.id);
        break;
      case 'next-level':
        this.startNextLevel();
        break;
      case 'toggle-audio':
        this.audioEnabled = !this.audioEnabled;
        this.audio.setEnabled(this.audioEnabled);
        this.ui.setAudioLabel(this.audioEnabled);
        break;
      case 'toggle-quality':
        this.highQuality = !this.highQuality;
        this.riverScene.renderer.shadowMap.enabled = this.highQuality;
        this.riverScene.renderer.setPixelRatio(Math.min(devicePixelRatio, this.highQuality ? 2 : 1));
        this.ui.setQualityLabel(this.highQuality);
        this.ui.toast(`已切换为${this.highQuality ? '高' : '低'}画质`);
        break;
      case 'fullscreen':
        this.toggleFullscreen();
        break;
      case 'reset-stats':
        if (confirm('确定清空本设备上的全部救援记录？')) {
          this.statistics.reset();
          this.ui.renderStats(this.statistics.data);
          this.ui.toast('本设备记录已清空');
        }
        break;
      case 'ring-pull':
        this.pullRing();
        break;
      case 'rope-tighten':
        this.tightenRope();
        break;
      case 'rope-pull':
        this.pullRope();
        break;
      case 'rope-loosen':
        this.flags.ropeError = true;
        this.attemptFailed('你及时松开了错误位置的绳索。换一件工具。');
        break;
      case 'rope-dangerous':
        this.flags.ropeError = true;
        this.flags.injury = true;
        this.flags.severe = true;
        this.finishRescue(false, null);
        break;
      case 'vest-step':
        this.handleVestStep(value ?? '');
        break;
      case 'pole-pull':
        this.resolvePole();
        break;
      case 'inflate':
        this.inflateBoat();
        break;
      case 'steer-left':
        this.boatDirection = -1;
        break;
      case 'steer-right':
        this.boatDirection = 1;
        break;
      case 'hold-action':
        break;
    }
  };

  private startLevel(id: string): void {
    this.currentLevel = getLevel(id);
    this.customSettings = null;
    this.customCharacter = 'random';
    this.availableTools = [...TOOL_ORDER];
    this.showTrajectory = true;
    this.allowSwitching = true;
    this.ui.root.classList.remove('strict-mode');
    this.beginSession();
  }

  private startCustom(settings: CustomSettings): void {
    if (!settings.tools.length) {
      this.ui.toast('至少保留一件救援工具', 'warning');
      return;
    }
    this.customSettings = settings;
    this.customCharacter = settings.character;
    this.currentLevel = {
      id: 'calm',
      index: 0,
      name: settings.strict ? '自定义严格河道' : '自定义河道',
      eyebrow: settings.strict ? '不显示风险和轨迹' : '你自己挖的坑',
      description: '参数已按你的设置生成，所有随机事件仍可由种子复现。',
      riverSpeed: settings.riverSpeed,
      wind: settings.wind,
      rescueTime: settings.rescueTime,
      distance: MathUtils.clamp(5.5 + settings.riverSpeed * 2.1, 6, 13),
      obstacles: settings.obstacles,
      storm: settings.riverSpeed > 3.2,
      recommended: 'lifeRing',
    };
    this.availableTools = [...settings.tools];
    this.showTrajectory = settings.showTrajectory && !settings.strict;
    this.allowSwitching = settings.allowSwitching && !settings.strict;
    this.ui.root.classList.toggle('strict-mode', settings.strict);
    this.beginSession();
  }

  private beginSession(): void {
    this.cleanupRound();
    this.ui.closePanel();
    this.ui.hideResult();
    this.ui.hideMenu();
    this.ui.showHud(false);
    this.ui.showTools(false);
    this.riverScene.configure(this.currentLevel);
    this.riverScene.cameraController.setMode('overview');
    this.usedTools = [];
    this.attempt = 1;
    this.multiIndex = 1;
    this.timeLeft = this.currentLevel.rescueTime;
    this.elapsedRescue = 0;
    this.outcomeRecorded = false;
    this.flags = freshFlags();
    this.createCharacter(true);
    this.setState(GameState.LEVEL_INTRO);
    this.ui.showIntro(this.currentLevel);
  }

  private createCharacter(forWalk: boolean): void {
    this.character?.group.removeFromParent();
    this.character?.dispose();
    const factory = new CharacterFactory(this.random);
    this.character = factory.create(this.currentLevel, this.customCharacter);
    this.riverScene.worldGroup.add(this.character.group);
    if (forWalk) this.character.placeForWalk();
    else this.character.placeInWater(this.currentLevel.distance);
  }

  private setState(state: GameState): void {
    this.previousState = this.state;
    this.state = state;
    this.stateElapsed = 0;
  }

  private updateState(delta: number): void {
    this.stateElapsed += delta;
    const introFactor = this.testMode ? 0.08 : 1;
    if (this.state === GameState.LEVEL_INTRO && this.stateElapsed > 1.75 * introFactor) {
      this.ui.hideIntro();
      this.riverScene.cameraController.setMode('follow');
      this.ui.setCaption('河边一切都很平静。暂时。');
      this.setState(GameState.CHARACTER_WALKING);
    } else if (this.state === GameState.CHARACTER_WALKING && this.stateElapsed > 2.6 * introFactor) {
      this.ui.setCaption(this.random.pick(ACCIDENTS));
      this.setState(GameState.ACCIDENT_TRIGGERED);
    } else if (this.state === GameState.ACCIDENT_TRIGGERED && this.stateElapsed > 1.1 * introFactor) {
      this.character?.setState(CharacterState.FALLING);
      this.ui.setCaption('等一下，这不是计划的一部分！');
      this.setState(GameState.CHARACTER_FALLING);
    } else if (this.state === GameState.CHARACTER_FALLING && this.character?.isFallComplete) {
      this.riverScene.splash(this.character.group.position);
      this.audio.splash();
      this.riverScene.cameraController.bump(0.35);
      this.enterWater();
    }

    if (this.state === GameState.RESCUE_SUCCESS) {
      if (this.character?.isTowComplete && this.character.state !== CharacterState.SAFE) {
        this.character.setState(CharacterState.SAFE);
        this.riverScene.celebrate(this.character.group.position);
      }
      if (this.stateElapsed > (this.testMode ? 0.1 : 2.8)) this.completeOutcome();
    }
    if (this.state === GameState.RESCUE_FAILURE && this.stateElapsed > (this.testMode ? 0.1 : 2.5)) {
      this.completeOutcome();
    }
  }

  private enterWater(): void {
    if (!this.character) return;
    this.ui.hideIntro();
    this.character.placeInWater(this.currentLevel.distance);
    this.riverScene.cameraController.setMode('decision');
    this.riverScene.cameraController.focus(this.character.group.position.clone().add(new Vector3(0, 1.4, 0)));
    this.ui.setCaption(this.character.definition.quip);
    this.ui.showHud(true);
    this.ui.showTools(true);
    this.ui.configureTools(this.availableTools, this.usedTools);
    this.ui.updateHud(this.hudData());
    this.setState(GameState.TOOL_SELECTION);
    setTimeout(() => {
      if (this.state === GameState.TOOL_SELECTION) this.ui.setCaption('');
    }, this.testMode ? 20 : 2100);
  }

  private selectTool(id: ToolId): void {
    if (![GameState.TOOL_SELECTION, GameState.SECONDARY_TOOL_SELECTION, GameState.AIMING].includes(this.state)) return;
    if (!this.availableTools.includes(id) || this.usedTools.includes(id)) {
      this.ui.toast('这件工具不可用或已经使用过', 'warning');
      return;
    }
    this.cleanupTool();
    this.selectedMethod = id;
    this.ui.selectTool(id);
    this.ui.showTools(false);
    const tool = createRescueTool(id);
    this.activeTool = tool;
    this.riverScene.effectsGroup.add(tool.group);
    if (id === 'rescuePole') {
      this.startPole();
      return;
    }
    if (id === 'inflatableBoat') {
      this.startBoat();
      return;
    }
    if (id === 'autoSalvage') {
      this.startAutoSalvage();
      return;
    }
    this.riverScene.cameraController.setMode('throw');
    const instruction =
      id === 'rope'
        ? '瞄准腰部附近，按住蓄力后松手'
        : id === 'lifeVest'
          ? '抛到伸手可及的位置，别让它漂走'
          : '落在身边最好，正中脑袋最糟';
    this.ui.showAim(id, instruction);
    this.setState(GameState.AIMING);
    this.updateTrajectory();
  }

  private cancelTool(): void {
    const cancellableInteraction =
      this.state === GameState.RESCUE_INTERACTION &&
      ['pole', 'boatInflate', 'autoSalvage'].includes(this.interactionMode);
    if (this.state !== GameState.AIMING && !cancellableInteraction) return;
    this.cleanupTool();
    this.ui.hideAim();
    this.ui.clearInteraction();
    this.interactionMode = 'none';
    if (this.character?.state === CharacterState.GRABBING_TOOL) {
      this.character.setState(CharacterState.SWIMMING);
    }
    this.ui.selectTool(null);
    this.ui.showTools(true);
    this.riverScene.cameraController.setMode('decision');
    this.setState(GameState.TOOL_SELECTION);
  }

  private beginCharge(): void {
    if (this.state !== GameState.AIMING || this.charging) return;
    this.charging = true;
    this.chargeDirection = 1;
  }

  private releaseCharge(): void {
    if (!this.charging || this.state !== GameState.AIMING) return;
    this.charging = false;
    this.launchTool();
  }

  private launchTool(): void {
    if (!this.activeTool || !this.character || !['lifeRing', 'rope', 'lifeVest'].includes(this.selectedMethod)) return;
    const target = this.character.group.position.clone();
    target.x += this.aimOffset * 9.5;
    target.y = 0.48;
    this.activeTool.launch(this.physics, {
      origin: THROW_ORIGIN,
      target,
      power: this.power,
      wind: this.currentLevel.wind,
    });
    if (this.activeTool instanceof RescueRope) this.activeTool.attachTo(this.riverScene.effectsGroup);
    this.ui.hideAim();
    this.riverScene.hideTrajectory();
    this.audio.throwWhoosh();
    this.setState(GameState.TOOL_IN_FLIGHT);
  }

  private inspectLanding(): void {
    if (!this.activeTool?.landingPoint || !this.character) return;
    this.riverScene.splash(this.activeTool.landingPoint);
    this.audio.splash();
    const distance = this.activeTool.landingPoint.distanceTo(this.character.group.position);
    this.flags.accuracy = MathUtils.clamp(1 - distance / 8.5, 0, 1);
    if (this.selectedMethod === 'lifeRing') this.landLifeRing(distance);
    else if (this.selectedMethod === 'rope') this.landRope();
    else if (this.selectedMethod === 'lifeVest') this.landVest();
  }

  private landLifeRing(distance: number): void {
    if (!this.character || !this.activeTool) return;
    const impactDanger = distance < 1.15 && this.activeTool.impactSpeed > Number(TOOL_CONFIG.lifeRing.safeImpactSpeed);
    if (impactDanger) {
      this.flags.injury = true;
      this.flags.dazed = true;
      this.character.setState(CharacterState.DAZED);
      this.ui.toast('救生圈是用来救人的，不是用来爆头的！', 'warning');
      this.attemptFailed('落水者被砸得眼冒金星。立刻换工具。');
      return;
    }
    if (this.flags.accuracy < 0.43) {
      this.attemptFailed('救生圈漂得很好，只是离目标很远。');
      return;
    }
    this.activeTool.state = ToolState.ATTACHED;
    this.character.setState(CharacterState.GRABBING_TOOL);
    this.interactionMode = 'ringPull';
    this.interactionCount = 0;
    this.ui.setInteraction(`
      <h3>抓住了！现在把人拉回来</h3>
      <p>连续点击回拉，太慢会被水流继续带走。</p>
      <div class="interaction-track"><i data-interaction-fill></i></div>
      <div class="interaction-actions"><button class="primary-button" type="button" data-action="ring-pull">用力回拉</button></div>
    `);
    this.setState(GameState.RESCUE_INTERACTION);
  }

  private pullRing(): void {
    if (this.interactionMode !== 'ringPull') return;
    this.interactionCount += 1;
    this.ui.setInteractionProgress(this.interactionCount / 6);
    this.audio.tone(230 + this.interactionCount * 35, 0.08, 'triangle', 0.1);
    if (this.interactionCount >= 6) {
      this.flags.timing = MathUtils.clamp(1 - this.stateElapsed / 7, 0.45, 1);
      this.resolveProbability('lifeRing');
    }
  }

  private landRope(): void {
    if (!this.character) return;
    if (this.flags.accuracy < 0.3) {
      this.attemptFailed('绳环套住了河水。河水拒绝配合。');
      return;
    }
    this.ropeSafe = this.flags.accuracy >= 0.62;
    this.interactionMode = 'ropeTighten';
    this.character.setState(this.ropeSafe ? CharacterState.REACHING : CharacterState.TANGLED);
    this.ui.setInteraction(`
      <h3>等待绳环落到安全位置</h3>
      <p>指示条接近黄色标记时点击收紧。位置不对就先松绳。</p>
      <div class="interaction-track"><i data-interaction-fill></i></div>
      <div class="interaction-actions"><button class="primary-button" type="button" data-action="rope-tighten">现在收紧</button></div>
    `);
    this.setState(GameState.RESCUE_INTERACTION);
  }

  private tightenRope(): void {
    if (this.interactionMode !== 'ropeTighten') return;
    const value = this.interactionValue;
    this.flags.timing = MathUtils.clamp(1 - Math.abs(value - 0.72) / 0.72, 0, 1);
    if (!this.ropeSafe) {
      this.flags.ropeError = true;
      this.interactionMode = 'ropeWrong';
      this.ui.setInteraction(`
        <h3>位置不对！先松绳！</h3>
        <p>绳环缠在危险位置。继续用力会造成严重救援失败。</p>
        <div class="interaction-actions">
          <button class="primary-button" type="button" data-action="rope-loosen">立即松绳</button>
          <button class="quiet-button danger-text" type="button" data-action="rope-dangerous">继续硬拉</button>
        </div>
      `);
      this.ui.toast('位置不对！先松绳！', 'warning');
      return;
    }
    this.interactionMode = 'ropePull';
    this.interactionCount = 0;
    this.ui.setInteraction(`
      <h3>安全套住腰部，快速回拉</h3>
      <p>连续点击，保持绳索张力。</p>
      <div class="interaction-track"><i data-interaction-fill></i></div>
      <div class="interaction-actions"><button class="primary-button" type="button" data-action="rope-pull">交替回拉</button></div>
    `);
  }

  private pullRope(): void {
    if (this.interactionMode !== 'ropePull') return;
    this.interactionCount += 1;
    this.ui.setInteractionProgress(this.interactionCount / 7);
    this.audio.tone(185 + (this.interactionCount % 2) * 45, 0.07, 'square', 0.08);
    if (this.interactionCount >= 7) this.resolveProbability('rope');
  }

  private landVest(): void {
    if (this.flags.accuracy < 0.45) {
      this.attemptFailed('救生衣漂得很好，人没有。');
      return;
    }
    this.character?.setState(CharacterState.WEARING_VEST);
    this.interactionMode = 'vestSequence';
    this.vestStep = 0;
    this.ui.setInteraction(`
      <h3>帮助落水者穿好救生衣</h3>
      <p>顺序很重要。标签朝外，不是套在脚上。</p>
      <div class="interaction-sequence">
        <button type="button" data-action="vest-step" data-value="arms">1 套入手臂</button>
        <button type="button" data-action="vest-step" data-value="straps">2 拉紧肩带</button>
        <button type="button" data-action="vest-step" data-value="buckle">3 扣上卡扣</button>
        <button type="button" data-action="vest-step" data-value="inflate">4 拉充气绳</button>
      </div>
    `);
    this.setState(GameState.RESCUE_INTERACTION);
  }

  private handleVestStep(step: string): void {
    if (this.interactionMode !== 'vestSequence') return;
    const sequence = ['arms', 'straps', 'buckle', 'inflate'];
    if (step !== sequence[this.vestStep]) {
      this.flags.vestError = true;
      this.ui.toast('顺序错了！标签朝外，不是套在脚上！', 'warning');
      this.attemptFailed('救生衣穿反后滑脱了。现在还能换工具。');
      return;
    }
    this.vestStep += 1;
    const button = this.ui.root.querySelector<HTMLElement>(`[data-action="vest-step"][data-value="${step}"]`);
    button?.classList.add('is-done');
    this.audio.tone(430 + this.vestStep * 90, 0.11, 'triangle', 0.11);
    if (this.vestStep === sequence.length) {
      this.flags.timing = MathUtils.clamp(1 - this.stateElapsed / 12, 0.52, 1);
      this.resolveProbability('lifeVest');
    }
  }

  private startPole(): void {
    if (!(this.activeTool instanceof RescuePole) || !this.character) return;
    const origin = new Vector3(this.character.group.position.x + 1.3, 2.4, 14.5);
    this.activeTool.extend(origin, this.character.group.position, 1);
    this.flags.accuracy = 0.78;
    this.interactionMode = 'pole';
    this.ui.setInteraction(`
      <h3>稳住救援杆</h3>
      <p>左右移动指针保持方向。距离超过 7 米就够不到。</p>
      <div class="interaction-track"><i data-interaction-fill></i></div>
      <div class="interaction-actions"><button class="primary-button" type="button" data-action="pole-pull">抓稳并回拉</button><button class="quiet-button" type="button" data-action="cancel-tool">换工具</button></div>
    `);
    this.setState(GameState.RESCUE_INTERACTION);
  }

  private resolvePole(): void {
    if (this.interactionMode !== 'pole' || !this.character) return;
    const reach = Math.abs(12.8 - this.character.group.position.z);
    if (reach > Number(TOOL_CONFIG.rescuePole.maxReach)) {
      this.attemptFailed('还差两厘米。可惜这里的两厘米大约有几米。');
      return;
    }
    this.flags.accuracy = this.interactionValue;
    this.flags.timing = MathUtils.clamp(1 - this.stateElapsed / 8, 0.45, 1);
    this.resolveProbability('rescuePole');
  }

  private startBoat(): void {
    if (!(this.activeTool instanceof InflatableBoat)) return;
    this.activeTool.group.position.set(12, 0.72, 11.5);
    this.interactionMode = 'boatInflate';
    this.interactionCount = 0;
    this.ui.setInteraction(`
      <h3>先把小船充满</h3>
      <p>快速点击充气。半充气的船只会提供半个笑话。</p>
      <div class="interaction-track"><i data-interaction-fill></i></div>
      <div class="interaction-actions"><button class="primary-button" type="button" data-action="inflate">快速充气</button></div>
    `);
    this.setState(GameState.RESCUE_INTERACTION);
  }

  private inflateBoat(): void {
    if (this.interactionMode !== 'boatInflate' || !(this.activeTool instanceof InflatableBoat)) return;
    this.interactionCount += 1;
    this.activeTool.inflate(0.105);
    this.ui.setInteractionProgress(this.activeTool.inflation);
    this.audio.tone(150 + this.interactionCount * 16, 0.08, 'sawtooth', 0.06);
    if (this.activeTool.inflation >= 1) {
      this.interactionMode = 'boatSteer';
      this.activeTool.group.position.set(12, 0.65, 10.8);
      this.ui.setInteraction(`
        <h3>小船下水，避开障碍接近目标</h3>
        <p>使用左右按钮或方向键操舵。</p>
        <div class="steer-row">
          <button class="steer-button" type="button" data-action="steer-left">向左</button>
          <button class="steer-button" type="button" data-action="steer-right">向右</button>
        </div>
      `);
    }
  }

  private startAutoSalvage(): void {
    if (!(this.activeTool instanceof AutoSalvageFleet) || !this.character) return;
    this.autoSalvageStart.copy(this.character.group.position);
    this.autoSalvageSoundPhase = '';
    this.activeTool.deploy(this.character.group.position);
    this.interactionMode = 'autoSalvage';
    this.ui.setInteraction(`
      <h3>自动打捞碟群已接管</h3>
      <p>无需瞄准，也无需蓄力。三架打捞碟正在扫描水流并计算安全吊点。</p>
      <div class="interaction-track"><i data-interaction-fill></i></div>
      <div class="auto-status"><span data-auto-phase>正在进场</span><b>全自动</b></div>
      <div class="interaction-actions"><button class="quiet-button" type="button" data-action="cancel-tool">取消自动打捞</button></div>
    `);
    this.riverScene.cameraController.bump(0.16);
    this.audio.tone(620, 0.18, 'sine', 0.11);
    this.setState(GameState.RESCUE_INTERACTION);
  }

  private observe(): void {
    if (![GameState.TOOL_SELECTION, GameState.SECONDARY_TOOL_SELECTION].includes(this.state) || !this.character) return;
    this.selectedMethod = 'observe';
    this.ui.showTools(false);
    this.interactionMode = 'observe';
    this.observationTimer = 0;
    this.ui.setInteraction(`
      <h3>先观察三秒</h3>
      <p>确认它是在求救，还是单纯在游泳。</p>
      <div class="interaction-track"><i data-interaction-fill></i></div>
    `);
    this.setState(GameState.RESCUE_INTERACTION);
  }

  private updateInteraction(delta: number): void {
    if (this.interactionMode === 'ropeTighten') {
      this.interactionValue = (Math.sin(this.stateElapsed * 4.2) + 1) / 2;
      this.ui.setInteractionProgress(this.interactionValue);
    } else if (this.interactionMode === 'pole') {
      this.interactionValue = MathUtils.clamp(1 - Math.abs(this.aimOffset) * 0.7 - Math.abs(Math.sin(this.totalTime * 2.4)) * 0.14, 0, 1);
      this.ui.setInteractionProgress(this.interactionValue);
      if (this.activeTool instanceof RescuePole && this.character) {
        const origin = new Vector3(this.character.group.position.x + 1.3, 2.4, 14.5);
        const target = this.character.group.position.clone().add(new Vector3(this.aimOffset * 4, 0, 0));
        this.activeTool.extend(origin, target, 1);
      }
    } else if (this.interactionMode === 'boatSteer' && this.activeTool instanceof InflatableBoat && this.character) {
      this.activeTool.steer(this.boatDirection, delta, this.currentLevel.riverSpeed);
      this.boatDirection *= Math.pow(0.08, delta);
      const distance = this.activeTool.group.position.distanceTo(this.character.group.position);
      if (distance < 3.1) {
        this.flags.accuracy = MathUtils.clamp(1 - distance / 4, 0.45, 1);
        this.flags.timing = MathUtils.clamp(1 - this.stateElapsed / this.currentLevel.rescueTime, 0.55, 1);
        if (this.character.definition.kind === 'cat' || this.character.definition.kind === 'orangeCat') {
          this.flags.accuracy -= 0.12;
        }
        this.resolveProbability('inflatableBoat');
      } else if (this.activeTool.group.position.x < this.character.group.position.x - 9) {
        this.attemptFailed('小船成功充气，然后成功漂走。');
      }
    } else if (
      this.interactionMode === 'autoSalvage' &&
      this.activeTool instanceof AutoSalvageFleet &&
      this.character
    ) {
      const telemetry = this.activeTool.updateAutomatic(
        delta,
        this.character.group.position,
      );
      this.ui.setInteractionProgress(telemetry.progress);
      const phaseLabel =
        telemetry.phase === 'deploying'
          ? '打捞碟群正在进场'
          : telemetry.phase === 'scanning'
            ? '正在扫描安全吊点'
            : telemetry.phase === 'lifting'
              ? '软质吊带已锁定'
              : '自动打捞完成';
      const phaseOutput = this.ui.root.querySelector<HTMLElement>('[data-auto-phase]');
      if (phaseOutput) phaseOutput.textContent = phaseLabel;

      if (this.autoSalvageSoundPhase !== telemetry.phase) {
        this.autoSalvageSoundPhase = telemetry.phase;
        const phaseTone =
          telemetry.phase === 'deploying'
            ? 620
            : telemetry.phase === 'scanning'
              ? 820
              : telemetry.phase === 'lifting'
                ? 520
                : 1040;
        this.audio.tone(phaseTone, 0.2, 'sine', 0.11);
      }

      if (telemetry.phase === 'lifting') {
        if (this.character.state !== CharacterState.GRABBING_TOOL) {
          this.autoSalvageStart.copy(this.character.group.position);
          this.character.setState(CharacterState.GRABBING_TOOL);
        }
        this.character.group.position.y = this.autoSalvageStart.y + telemetry.lift * 2.6;
      }

      if (telemetry.complete) this.resolveAutoSalvage();
    } else if (this.interactionMode === 'observe' && this.character) {
      this.observationTimer += delta;
      this.ui.setInteractionProgress(this.observationTimer / 3);
      if (this.observationTimer >= (this.testMode ? 0.05 : 3)) {
        if (this.character.definition.swimSkill >= 0.9) {
          this.flags.accuracy = 1;
          this.flags.timing = 1;
          this.finishRescue(true, null);
        } else {
          this.ui.toast('它确实需要救。观察结束，赶紧选工具。', 'warning');
          this.character.stamina = Math.max(0.05, this.character.stamina - 0.08);
          this.interactionMode = 'none';
          this.ui.clearInteraction();
          this.ui.showTools(true);
          this.setState(GameState.TOOL_SELECTION);
        }
      }
    }
  }

  private resolveAutoSalvage(): void {
    if (
      this.interactionMode !== 'autoSalvage' ||
      !(this.activeTool instanceof AutoSalvageFleet) ||
      !this.character
    ) {
      return;
    }
    this.interactionMode = 'none';
    this.flags.accuracy = MathUtils.clamp(
      0.97 -
        this.currentLevel.riverSpeed * 0.02 -
        this.currentLevel.wind * 0.02 -
        this.currentLevel.obstacles * 0.02,
      0.58,
      0.96,
    );
    this.flags.timing = MathUtils.clamp(
      1 - this.stateElapsed / Math.max(16, this.currentLevel.rescueTime * 2),
      0.82,
      0.98,
    );
    const result = evaluateRescue(
      {
        tool: 'autoSalvage',
        accuracy: this.flags.accuracy,
        timing: this.flags.timing,
        stamina: this.character.stamina,
        riverSpeed: this.currentLevel.riverSpeed,
        wind: this.currentLevel.wind,
        obstaclePenalty: this.currentLevel.obstacles * 0.018,
        injuryPenalty: 0,
        character: this.character.definition,
      },
      this.random,
    );
    this.ui.showDebug(
      this.debug
        ? {
            seed: this.random.seed,
            state: this.state,
            chance: result.chance,
            roll: result.roll,
            accuracy: this.flags.accuracy,
            timing: this.flags.timing,
            automatic: 1,
          }
        : null,
    );
    if (result.success) {
      this.finishRescue(true, result);
    } else {
      this.character.group.position.copy(this.autoSalvageStart);
      this.character.setState(CharacterState.SWIMMING);
      this.attemptFailed('急流和障碍干扰了自动吊点，碟群已安全撤离。可以立即换工具。');
    }
  }

  private resolveProbability(tool: ToolId): void {
    if (!this.character) return;
    const result = evaluateRescue(
      {
        tool,
        accuracy: this.flags.accuracy,
        timing: this.flags.timing,
        stamina: this.character.stamina,
        riverSpeed: this.currentLevel.riverSpeed,
        wind: this.currentLevel.wind,
        obstaclePenalty: this.currentLevel.obstacles * 0.012,
        injuryPenalty: this.flags.injury ? 0.24 : 0,
        character: this.character.definition,
      },
      this.random,
    );
    this.ui.showDebug(
      this.debug
        ? {
            seed: this.random.seed,
            state: this.state,
            chance: result.chance,
            roll: result.roll,
            accuracy: this.flags.accuracy,
            timing: this.flags.timing,
            river: this.currentLevel.riverSpeed,
          }
        : null,
    );
    if (result.success) this.finishRescue(true, result);
    else this.attemptFailed(`操作完成了，但综合成功率只有 ${Math.round(result.chance * 100)}%。`);
  }

  private attemptFailed(message: string): void {
    this.ui.hideAim();
    this.ui.clearInteraction();
    this.riverScene.hideTrajectory();
    this.interactionMode = 'none';
    if (this.selectedMethod !== 'observe' && !this.usedTools.includes(this.selectedMethod)) {
      this.usedTools.push(this.selectedMethod);
    }
    const canRetry =
      this.allowSwitching &&
      this.attempt <= GAME_CONFIG.maxSecondaryAttempts &&
      this.timeLeft > 2.2 &&
      this.availableTools.some((tool) => !this.usedTools.includes(tool));
    if (canRetry) {
      this.timeLeft = Math.max(0, this.timeLeft - GAME_CONFIG.toolSwitchPenalty);
      this.attempt += 1;
      this.ui.toast(message, 'warning');
      this.ui.configureTools(this.availableTools, this.usedTools);
      this.ui.selectTool(null);
      this.ui.showTools(true);
      this.riverScene.cameraController.setMode('decision');
      this.setState(GameState.SECONDARY_TOOL_SELECTION);
    } else {
      this.finishRescue(false, null);
    }
  }

  private finishRescue(success: boolean, probability: ProbabilityResult | null): void {
    if (!this.character || this.outcomePending) return;
    this.outcomePending = { success, probability };
    this.ui.showTools(false);
    this.ui.hideAim();
    this.ui.clearInteraction();
    this.riverScene.hideTrajectory();
    this.interactionMode = 'none';
    this.audio.setTension(1);
    if (success) {
      this.character.setState(CharacterState.BEING_TOWED);
      this.riverScene.cameraController.setMode('result');
      this.audio.success();
      this.ui.setCaption('抓稳！最后一段路。');
      this.setState(GameState.RESCUE_SUCCESS);
    } else {
      this.character.setState(CharacterState.LOST_DOWNSTREAM);
      this.riverScene.cameraController.setMode('result');
      this.audio.failure();
      if (this.flags.severe) {
        this.audio.siren();
        this.ui.setCaption('画面切到远景。专业救援人员正在赶来。');
      } else {
        this.ui.setCaption('救援队正在下游追赶。');
      }
      this.setState(GameState.RESCUE_FAILURE);
    }
  }

  private completeOutcome(): void {
    if (!this.outcomePending || !this.character) return;
    if (this.outcomePending.success && (this.currentLevel.rescueCount ?? 1) > this.multiIndex) {
      this.multiIndex += 1;
      if (this.selectedMethod !== 'observe' && !this.usedTools.includes(this.selectedMethod)) {
        this.usedTools.push(this.selectedMethod);
      }
      this.outcomePending = null;
      this.cleanupTool();
      this.createCharacter(false);
      this.timeLeft = this.currentLevel.rescueTime;
      this.attempt = 1;
      this.ui.setCaption('');
      this.ui.toast(`连续救援 ${this.multiIndex}/${this.currentLevel.rescueCount}，上一件工具已消耗`, 'success');
      this.ui.configureTools(this.availableTools, this.usedTools);
      this.ui.showHud(true);
      this.ui.showTools(true);
      this.riverScene.cameraController.setMode('decision');
      this.setState(GameState.TOOL_SELECTION);
      return;
    }
    this.showResult();
  }

  private showResult(): void {
    if (!this.outcomePending || !this.character || this.outcomeRecorded) return;
    const success = this.outcomePending.success;
    const method = this.selectedMethod;
    const recommended = this.character.definition.bestTools[0] as ToolId | 'observe';
    const score = calculateScore({
      success,
      elapsed: this.elapsedRescue,
      rescueTime: this.currentLevel.rescueTime,
      accuracy: this.flags.accuracy,
      timing: this.flags.timing,
      tool: method,
      recommended,
      secondaryAttempts: this.attempt - 1,
      injury: this.flags.injury,
      stamina: this.character.stamina,
    });
    const perfect = success && score.total >= 900 && !this.flags.injury;
    const title = success
      ? this.successTitle(perfect)
      : this.flags.severe
        ? '严重救援失败，画面已切到远景。'
        : this.failureTitle();
    const copy = success
      ? `用时 ${this.elapsedRescue.toFixed(1)} 秒，${this.character.definition.name}安全回到岸边。`
      : '目标被水流带向下游，卡通救援队已经接手。';
    this.statistics.record({
      success,
      perfect,
      tool: method === 'observe' ? null : method,
      elapsed: this.elapsedRescue,
      animal: this.character.definition.animal,
      score: score.total,
      dazed: this.flags.dazed,
      ropeError: this.flags.ropeError,
      vestError: this.flags.vestError,
    });
    this.outcomeRecorded = true;
    this.ui.showHud(false);
    this.ui.setCaption('');
    this.ui.showResult({
      success,
      title,
      copy,
      quip: this.character.definition.quip,
      tool: method,
      elapsed: this.elapsedRescue,
      stamina: this.character.stamina,
      score,
      seed: this.random.seed,
      secondaryAttempts: this.attempt - 1,
    });
    this.setState(GameState.RESULT_SCREEN);
  }

  private successTitle(perfect: boolean): string {
    if (!this.character) return '救援成功。';
    if (this.character.definition.kind === 'cat' || this.character.definition.kind === 'orangeCat') {
      return '猫虽然获救，但它明显不感谢你。';
    }
    if (this.character.definition.kind === 'panda') return '熊猫安全上岸，现在轮到你喘气了。';
    if (this.character.definition.kind === 'duck') return '你救了鸭子，但鸭子认为这是多此一举。';
    if (this.selectedMethod === 'autoSalvage') return '三架打捞碟完成任务，你全程没有手抖。';
    return perfect ? '完美！没有人被道具砸中。' : '人救上来了，道具也没丢。';
  }

  private failureTitle(): string {
    if (this.flags.dazed) return '救生圈准确命中了目标，可惜目标是脑袋。';
    if (this.flags.ropeError) return '绳子很结实，问题是套错了地方。';
    if (this.flags.vestError) return '救生衣漂得很好，人没有。';
    if (this.selectedMethod === 'inflatableBoat') return '小船成功充气，然后成功漂走。';
    if (this.selectedMethod === 'autoSalvage') return '自动打捞没有失手，只是河道拒绝配合。';
    if (this.selectedMethod === 'rescuePole') return '救援杆离目标只差永远的一点点。';
    return '你成功把简单问题变复杂了。';
  }

  private updateRescue(delta: number): void {
    if (!this.character) return;
    const activeStates = [
      GameState.TOOL_SELECTION,
      GameState.AIMING,
      GameState.TOOL_IN_FLIGHT,
      GameState.TOOL_IN_WATER,
      GameState.RESCUE_INTERACTION,
      GameState.SECONDARY_TOOL_SELECTION,
    ];
    if (!activeStates.includes(this.state)) return;
    this.timeLeft = Math.max(0, this.timeLeft - delta);
    this.elapsedRescue += delta;
    const drain =
      (0.018 + this.currentLevel.riverSpeed * 0.006 + this.character.definition.panicLevel * 0.008) *
      (1.15 - this.character.definition.swimSkill * 0.35);
    this.character.stamina = Math.max(0, this.character.stamina - delta * drain);
    if (this.character.stamina < 0.35 && this.character.state === CharacterState.SWIMMING) {
      this.character.setState(CharacterState.PANICKING);
    }
    this.audio.setTension(this.character.stamina);
    this.ui.updateHud(this.hudData());
    if (this.timeLeft <= 0 || this.character.stamina <= 0.01 || this.character.group.position.x > 58) {
      this.finishRescue(false, null);
    }
  }

  private hudData() {
    const character = this.character!;
    return {
      stamina: character.stamina,
      distance: Math.max(0, 12.8 - character.group.position.z),
      riverSpeed: this.currentLevel.riverSpeed,
      timeLeft: this.timeLeft,
      selectedTool: this.selectedMethod === 'observe' ? null : this.selectedMethod,
      attempt: this.attempt,
      characterName: character.definition.name,
    };
  }

  private updateAim(delta: number): void {
    if (this.state !== GameState.AIMING) return;
    if (this.charging) {
      this.power += delta * 0.72 * this.chargeDirection;
      if (this.power >= 1) {
        this.power = 1;
        this.chargeDirection = -1;
      } else if (this.power <= 0.08) {
        this.power = 0.08;
        this.chargeDirection = 1;
      }
      this.ui.setPower(this.power);
    }
    this.updateTrajectory();
  }

  private updateTrajectory(): void {
    if (this.state !== GameState.AIMING || !this.character) return;
    const target = this.character.group.position.clone();
    target.x += this.aimOffset * 9.5;
    target.y = 0.48;
    this.riverScene.showTrajectory(
      THROW_ORIGIN,
      target,
      this.power,
      this.currentLevel.wind,
      this.showTrajectory,
    );
  }

  private loop = (now: number): void => {
    const delta = Math.min(0.05, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    if (this.state !== GameState.PAUSED) {
      this.totalTime += delta;
      this.physics.step(1 / 60, delta, 3);
      this.updateState(delta);
      this.updateRescue(delta);
      this.updateAim(delta);
      this.updateInteraction(delta);
      this.character?.update(delta, this.currentLevel.riverSpeed, this.totalTime);
      if (
        this.character &&
        [
          GameState.TOOL_SELECTION,
          GameState.AIMING,
          GameState.TOOL_IN_FLIGHT,
          GameState.TOOL_IN_WATER,
          GameState.RESCUE_INTERACTION,
          GameState.SECONDARY_TOOL_SELECTION,
        ].includes(this.state)
      ) {
        this.riverScene.cameraController.focus(
          this.character.group.position
            .clone()
            .add(new Vector3(0, this.interactionMode === 'autoSalvage' ? 3.5 : 1.1, 0)),
        );
      }
      if (this.activeTool) {
        const priorState = this.activeTool.state;
        this.activeTool.update(delta, this.currentLevel.riverSpeed);
        if (priorState === ToolState.THROWN && this.activeTool.state === ToolState.FLOATING) {
          this.setState(GameState.TOOL_IN_WATER);
          this.inspectLanding();
        }
      }
      this.riverScene.update(this.totalTime, delta, this.currentLevel.riverSpeed);
    }
    this.riverScene.render();
    this.animationFrame = requestAnimationFrame(this.loop);
  };

  private pause(): void {
    if ([GameState.MENU, GameState.PAUSED, GameState.RESULT_SCREEN].includes(this.state)) return;
    this.previousState = this.state;
    this.state = GameState.PAUSED;
    this.ui.pause(true);
  }

  private resume(): void {
    if (this.state !== GameState.PAUSED) return;
    this.state = this.previousState;
    this.lastFrame = performance.now();
    this.ui.pause(false);
  }

  private goHome(): void {
    this.cleanupRound();
    this.ui.closePanel();
    this.ui.hideResult();
    this.ui.showHud(false);
    this.ui.showTools(false);
    this.ui.hideAim();
    this.ui.clearInteraction();
    this.riverScene.cameraController.setMode('overview');
    this.ui.showMenu();
    this.setState(GameState.MENU);
  }

  private startNextLevel(): void {
    const index = Math.max(0, LEVELS.findIndex((level) => level.id === this.currentLevel.id));
    this.startLevel(LEVELS[(index + 1) % LEVELS.length].id);
  }

  private cleanupTool(): void {
    if (!this.activeTool) return;
    this.activeTool.group.removeFromParent();
    this.activeTool.dispose();
    this.activeTool = null;
    this.charging = false;
    this.power = 0;
    this.ui.setPower(0);
  }

  private cleanupRound(): void {
    this.cleanupTool();
    this.ui.clearInteraction();
    this.ui.hideAim();
    this.ui.selectTool(null);
    this.ui.showTools(false);
    this.character?.group.removeFromParent();
    this.character?.dispose();
    this.character = null;
    this.outcomePending = null;
    this.outcomeRecorded = false;
    this.interactionMode = 'none';
    this.ui.hideIntro();
    this.ui.setCaption('');
    this.riverScene.hideTrajectory();
  }

  private toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => this.ui.toast('浏览器阻止了全屏请求', 'warning'));
    } else {
      document.exitFullscreen().catch(() => undefined);
    }
  }

  private onResize = (): void => this.riverScene.resize();

  private onPointerMove = (event: PointerEvent): void => {
    if (![GameState.AIMING, GameState.RESCUE_INTERACTION].includes(this.state)) return;
    this.aimOffset = MathUtils.clamp((event.clientX / window.innerWidth - 0.5) * 2, -1, 1);
  };

  private onChargeStart = (event: Event): void => {
    event.preventDefault();
    this.beginCharge();
  };

  private onChargeEnd = (event: Event): void => {
    event.preventDefault();
    this.releaseCharge();
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat && event.code !== 'Space') return;
    const keyMap: Record<string, ToolId> = {
      KeyA: 'lifeRing',
      KeyB: 'rope',
      KeyC: 'lifeVest',
      KeyD: 'rescuePole',
      KeyE: 'inflatableBoat',
      KeyF: 'autoSalvage',
    };
    if (keyMap[event.code]) {
      this.selectTool(keyMap[event.code]);
      return;
    }
    if (event.code === 'Space') {
      event.preventDefault();
      if (this.state === GameState.MENU) this.startLevel('calm');
      else this.beginCharge();
    } else if (event.code === 'KeyP' || event.code === 'Escape') {
      if (this.state === GameState.PAUSED) this.resume();
      else this.pause();
    } else if (event.code === 'ArrowLeft') {
      this.aimOffset = Math.max(-1, this.aimOffset - 0.12);
      this.boatDirection = -1;
    } else if (event.code === 'ArrowRight') {
      this.aimOffset = Math.min(1, this.aimOffset + 0.12);
      this.boatDirection = 1;
    }
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    if (event.code === 'Space') this.releaseCharge();
  };

  private onVisibilityChange = (): void => {
    if (document.hidden) this.pause();
  };

  private snapshot(): Record<string, unknown> {
    return {
      state: this.state,
      seed: this.random.seed,
      level: this.currentLevel.id,
      character: this.character?.definition.kind ?? null,
      stamina: this.character?.stamina ?? 0,
      timeLeft: this.timeLeft,
      attempt: this.attempt,
      selectedMethod: this.selectedMethod,
      interactionMode: this.interactionMode,
      toolState: this.activeTool?.state ?? null,
      toolY: this.activeTool?.body?.position.y ?? this.activeTool?.group.position.y ?? null,
      toolVelocityY: this.activeTool?.body?.velocity.y ?? null,
      dazed: this.flags.dazed,
      ropeError: this.flags.ropeError,
      vestError: this.flags.vestError,
      statsTotal: this.statistics.data.total,
    };
  }

  dispose(): void {
    cancelAnimationFrame(this.animationFrame);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.cleanupRound();
    this.audio.dispose();
    this.riverScene.dispose();
    this.ui.dispose();
    delete window.__RESCUE_GAME__;
  }
}
